import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';

interface GoogleLoginButtonProps {
  isRecruiter?: boolean;
  disabled?: boolean;
  className?: string;
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({ 
  isRecruiter = false, 
  disabled = false,
  className = ''
}) => {
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = async () => {
    setLoading(true);
    
    try {
      // Store recruiter flag in sessionStorage for after OAuth callback
      if (isRecruiter) {
        sessionStorage.setItem('google_signup_recruiter', 'true');
      } else {
        sessionStorage.removeItem('google_signup_recruiter');
      }
      
      // Store return URL for redirect after login (unless recruiter signup)
      if (!isRecruiter) {
        // Check if there's a 'return' query param (from signup/login pages)
        const urlParams = new URLSearchParams(window.location.search);
        const returnParam = urlParams.get('return');
        
        // Auth pages that should NOT be stored as return URLs
        const authPages = ['/login', '/signup', '/signup-choice', '/recruiter-signup', '/email-confirmation'];
        const isAuthPage = authPages.some(page => window.location.pathname.startsWith(page));
        
        // Use the return param if exists, otherwise use current URL (unless on auth page)
        if (returnParam) {
          sessionStorage.setItem('google_return_url', returnParam);
        } else if (!isAuthPage) {
          sessionStorage.setItem('google_return_url', window.location.pathname + window.location.search);
        }
        // If on auth page without return param, don't store anything (will go to home)
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('Google login error:', error);
        setLoading(false);
      }
      // If no error, user will be redirected to Google
    } catch (err) {
      console.error('Google login error:', err);
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGoogleLogin}
      disabled={disabled || loading}
      className={`w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
    >
      {/* Google Logo SVG */}
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      {loading ? t('googleLogin.loading', 'Connecting...') : t('googleLogin.button', 'Continue with Google')}
    </button>
  );
};

export default GoogleLoginButton;
